import React from 'react'
import { Box, Divider, Grid, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import BasicModal from './BasicModal'
import ContainedButton, {
  ContainedButtonVariant,
} from '../Buttons/ContainedButton'
import { InvitationSource, PotentialInviteItem } from '@swagger'
import { dateToDashString, IS_UTC } from '@utils/dateUtility'
import fullName from '@utils/fullName'
import {
  getSourceLabel,
  getStatusLabel,
} from '../Communities/getAwaitingDecisionsTableColumns'
import { maskEmail } from '@utils/maskEmail'

interface FamilyDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  potentialInvite: PotentialInviteItem
  source: InvitationSource
}

const LabeledField: React.FC<{
  label: string
  value?: React.ReactNode
}> = ({ label, value }) => {
  const theme = useTheme()
  return (
    <Box mb={2}>
      <Typography
        variant="overline"
        component="p"
        sx={{
          display: 'block',
          color: theme.palette.primary.main,
          marginBottom: theme.spacing(0.5),
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        component="div"
        sx={{
          color: theme.palette.text.primary,
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        }}
      >
        {value ?? '—'}
      </Typography>
    </Box>
  )
}

const SectionHeader: React.FC<{ title: string }> = ({ title }) => {
  const theme = useTheme()
  return (
    <Box mt={1} mb={2}>
      <Typography
        variant="subtitle2"
        component="h3"
        sx={{
          fontWeight: 700,
          color: theme.palette.text.primary,
        }}
      >
        {title}
      </Typography>
      <Divider sx={{ mt: 0.5 }} />
    </Box>
  )
}

const FamilyDetailsModal: React.FC<FamilyDetailsModalProps> = ({
  isOpen,
  onClose,
  potentialInvite,
  source,
}) => {
  const { t } = useTranslation()

  const isLcdReferral = potentialInvite.source === InvitationSource.LcdReferral

  /** Collect all unique emails: contactEmail + crmEmails */
  const uniqueEmails = Array.from(
    new Set(
      [
        potentialInvite.contactEmail,
        ...(potentialInvite.crmEmails ?? []),
      ].filter((email): email is string => Boolean(email))
    )
  )

  const dialogContent = (
    <Box>
      {/* Contact Information */}
      <SectionHeader
        title={t(
          'FamilyDetailsModal.Section.ContactInformation',
          'Contact Information'
        )}
      />

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <LabeledField
            label={t('FamilyDetailsModal.Field.FirstName', 'First Name')}
            value={potentialInvite.parentFirstName}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <LabeledField
            label={t('FamilyDetailsModal.Field.LastName', 'Last Name')}
            value={potentialInvite.parentLastName}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <LabeledField
            label={t('FamilyDetailsModal.Field.Phone', 'Phone')}
            value={potentialInvite.contactPhone}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <LabeledField
            label={t('FamilyDetailsModal.Field.Location', 'Location')}
            value={potentialInvite.location}
          />
        </Grid>
      </Grid>

      <LabeledField
        label={t('FamilyDetailsModal.Field.Email', 'Email')}
        value={
          uniqueEmails.length > 0 ? (
            <Box>
              {uniqueEmails.map((email) => (
                <Typography key={email} variant="body2" component="p">
                  {source === InvitationSource.ReturningFamily
                    ? maskEmail(email)
                    : email}
                </Typography>
              ))}
            </Box>
          ) : null
        }
      />

      {/* Children */}
      {potentialInvite.children.length > 0 && (
        <>
          <SectionHeader
            title={t(
              'FamilyDetailsModal.Section.Children',
              'Children ({{count}})',
              {
                count: potentialInvite.children.length,
              }
            )}
          />
          <Box mb={2}>
            <Grid container spacing={2}>
              {potentialInvite.children.map((child) => (
                <Grid item xs={12} sm={6} key={child.potentialInviteChildKey}>
                  <Typography variant="body2">{fullName(child)}</Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
        </>
      )}

      {/* Source */}
      <SectionHeader title={t('FamilyDetailsModal.Section.Source', 'Source')} />

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <LabeledField
            label={t('FamilyDetailsModal.Field.Source', 'Source')}
            value={getSourceLabel(t, potentialInvite.source)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <LabeledField
            label={t('FamilyDetailsModal.Field.Status', 'Status')}
            value={getStatusLabel(t, potentialInvite.status)}
          />
        </Grid>
      </Grid>

      {isLcdReferral && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <LabeledField
                label={t('FamilyDetailsModal.Field.ReferredOn', 'Referred On')}
                value={
                  potentialInvite.referredOn
                    ? dateToDashString(potentialInvite.referredOn, IS_UTC)
                    : null
                }
              />
            </Grid>
          </Grid>

          {/* Referral Note – only for LCD Referral */}
          <SectionHeader
            title={t(
              'FamilyDetailsModal.Section.ReferralNote',
              'Referral Note'
            )}
          />
          <Typography variant="body2" mb={2} sx={{ whiteSpace: 'pre-wrap' }}>
            {potentialInvite.referralNote ||
              t('FamilyDetailsModal.NoReferralNote', '—')}
          </Typography>
        </>
      )}
    </Box>
  )

  return (
    <BasicModal
      isOpen={isOpen}
      maxWidth="xs"
      handleFormSubmit={(e) => e.preventDefault()}
      ariaLabel="family details"
      dialogTitle={t('FamilyDetailsModal.Title', 'Family Details')}
      dialogContent={dialogContent}
      dialogActions={
        <Box display="flex" justifyContent="center" width="100%">
          <ContainedButton
            variant={ContainedButtonVariant.Close}
            id="familyDetailsClose"
            onClick={onClose}
          />
        </Box>
      }
    />
  )
}

export default FamilyDetailsModal
